import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MdDragIndicator, MdCalendarToday } from "react-icons/md";
import { IoCheckmarkCircle, IoEllipseOutline } from "react-icons/io5";
import { useDispatch } from "react-redux";
import { updateTask } from "../../../Redux/slices/TaskSlice";

const TaskItem = ({ task, onClick, isSortable }) => {
    const dispatch = useDispatch();

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: task._id, disabled: !isSortable });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : 'auto',
    };

    const handleToggleComplete = (e) => {
        e.stopPropagation();
        dispatch(updateTask({ taskId: task._id, updates: { isCompleted: !task.isCompleted } }));
    };

    // Deadline Logic
    const getDeadlineBadge = () => {
        if (!task.dueDate) return null;

        const today = new Date();
        const due = new Date(task.dueDate);
        const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));

        let classes = "bg-indigo-50 text-indigo-600"; // Default far out

        if (diffDays < 0 || diffDays === 0) {
            classes = "bg-indigo-600 text-white"; // Overdue/Today
        } else if (diffDays <= 3) {
            classes = "bg-indigo-200 text-indigo-800"; // Approaching
        }

        return (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${classes}`}>
                <MdCalendarToday size={10} />
                {due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </span>
        );
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`group bg-white rounded-xl p-3 mb-2 border border-zinc-100 shadow-sm hover:border-indigo-200 hover:shadow-md transition-all flex items-center gap-3 relative cursor-pointer ${task.isCompleted ? 'opacity-50' : 'opacity-100'}`}
            onClick={onClick}
        >
            {/* Drag Handle */}
            {isSortable && (
                <div {...attributes} {...listeners} className="text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing p-1 -ml-1">
                    <MdDragIndicator size={16} />
                </div>
            )}

            {/* Custom Round Checkbox */}
            <button
                onClick={handleToggleComplete}
                className={`flex-shrink-0 w-5 h-5 rounded-full border-2 transition-all flex items-center justify-center ${task.isCompleted
                    ? 'bg-indigo-600 border-indigo-600'
                    : 'bg-transparent border-zinc-300 hover:border-indigo-500'
                    }`}
            >
                {task.isCompleted && <IoCheckmarkCircle className="text-white w-4 h-4" />}
            </button>

            {/* Content */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                    <h4 className={`text-[14px] font-medium truncate ${task.isCompleted ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                        {task.title}
                    </h4>
                    {getDeadlineBadge()}
                </div>
                {task.description && (
                    <p className="text-xs text-gray-500 truncate max-w-[90%]">{task.description}</p>
                )}
            </div>

            {/* Right Side Info */}
            <div className="flex items-end flex-col gap-1">
                {/* Assignee Avatar */}
                {task.assignedUserId && (
                    <div className="relative">
                        {task.assignedUserId.userImage ? (
                            <img src={task.assignedUserId.userImage} alt="Assignee" className="w-6 h-6 rounded-full object-cover border border-white shadow-sm" />
                        ) : (
                            <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-600 border border-white">
                                {task.assignedUserId.name?.[0] || 'U'}
                            </div>
                        )}
                        {/* Pending Indicator */}
                        {task.assignmentStatus === 'pending' && (
                            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-yellow-400 rounded-full border border-white"></span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TaskItem;
